import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Collapse,
  Badge,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  ShoppingCart as OrdersIcon,
  LocalShipping as DeliveryIcon,
  Inventory as PickingIcon,
  Sync as ReconciliationIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  AltRoute as RoutingIcon,
  Map as MapIcon,
  DirectionsCar as VehiclesIcon,
  AssignmentReturn as ManifestsIcon,
  Person as DriversIcon,
  Timeline as TimetablesIcon,
  LocationOn as ZonesIcon,
  People as CustomersIcon,
  Inventory as InventoryIconMui,
  ExpandLess,
  ExpandMore,
  LocalOffer as TagIcon,
  ErrorOutline as ErrorIcon,
  LocalShipping as FleetIcon,
  LocalShipping as BulkIcon,
  LocalShipping as TruckIcon,
  Store as StoreIcon,
  List as AllOrdersIcon,
  Add as NewIcon,
  PlayArrow as StartIcon,
  Inventory2 as PickingStatusIcon,
  Inbox as PackingIcon,
  HourglassEmpty as PendingIcon,
  LocalShipping as DispatchedIcon,
  CheckCircle as ReadyIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Block as UndeliveredIcon,
  Payment as PaymentIcon,
  LocalShipping as FreightIcon,
  LocationOff as AddressIssueIcon,
  AccountCircle as ProfileIcon,
  ExitToApp as LogoutIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  DriveEta as DriverDashboardIcon,
  Route as RouteIcon,
  Assignment as DeliveryTaskIcon,
  Description as QuoteIcon,
  Work as WorkIcon,
  CheckCircle as SentIcon,
  FollowTheSigns as FollowUpIcon,
  Unpublished as UnallocatedIcon,
  Payment as AwaitingPaymentIcon,
  Inventory as AwaitingStockIcon,
  PersonOff as CustomerIssueIcon,
  LocalShipping as FreightIssueIcon,
  Build as InternalIssueIcon,
  Group as TeamIcon,
  Assessment as ReportsIcon,
  LocalShipping as CourierIcon,
  AccountBalance as MyobIcon,
  Storefront as StorefrontIcon,
  LocalShipping as ShippingIcon,
  Warehouse as WarehouseIcon,
  FactCheck as StockCountIcon,
  HealthAndSafety as HealthCheckIcon,
  ReceiptLong as InvoicingIcon,
  RequestQuote as StatementIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material';
import { useOrderCounts } from '../contexts/OrderCountsContext';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

const expandedDrawerWidth = 272;
const collapsedDrawerWidth = 68;

// Enterprise color palette
const sidebarColors = {
  bg: '#0f172a',
  bgHover: '#1e293b',
  bgActive: 'rgba(45, 157, 157, 0.15)',
  text: '#94a3b8',
  textActive: '#ffffff',
  accent: '#2d9d9d',
  border: '#1e293b',
  headerBg: '#0c1322',
};

interface LayoutProps {
  children: React.ReactNode;
}

// Dynamic order counts will be fetched from API

// Color scheme for different order types
const orderTypeColors = {
  quote: '#ff9800', // Orange
  onHold: '#9c27b0', // Purple
  backorder: '#795548', // Brown
  cancelled: '#f44336', // Red  
  returns: '#2196f3', // Blue
  sydneyFleet: '#4caf50', // Green
  sydneyFleetBulk: '#4caf50', // Green
  borderExpress: '#ff9800', // Orange
  borderExpressBulk: '#ff9800', // Orange
  courierPlease: '#009688', // Teal
  vellex: '#673ab7', // Deep Purple
  mainfreight: '#1565c0', // Blue
  pickupInstore: '#795548', // Brown
};

const topLevelItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Map', icon: <MapIcon />, path: '/map' },
  { text: 'Fleet Live', icon: <MapIcon />, path: '/fleet-map' },
  { text: 'Quotes', icon: <QuoteIcon />, path: '/quotes', hasSubItems: true },
  { text: 'Orders', icon: <OrdersIcon />, path: '/orders', hasSubItems: true },
  { text: 'Picking', icon: <PickingIcon />, path: '/picking' },
  { text: 'Manifests', icon: <ManifestsIcon />, path: '/manifests' },
  { text: 'Delivery', icon: <DeliveryIcon />, path: '/delivery' },
  { text: 'Customers', icon: <CustomersIcon />, path: '/customers' },
  { text: 'Products', icon: <InventoryIconMui />, path: '/products' },
  { text: 'Inventory', icon: <InventoryIconMui />, path: '/inventory' },
  { text: '3PL', icon: <WarehouseIcon />, path: '/3pl', hasSubItems: true },
  { text: 'Hub', icon: <WorkIcon />, path: '/hub', hasSubItems: true },
  { text: 'Vehicles', icon: <VehiclesIcon />, path: '/vehicles' },
  { text: 'Timetables', icon: <TimetablesIcon />, path: '/timetables' },
  { text: 'Zones', icon: <ZonesIcon />, path: '/zones' },
  { text: 'Warehouse Zones', icon: <WarehouseIcon />, path: '/warehouse-zones' },
  { text: 'Drivers', icon: <DriversIcon />, path: '/drivers' },
  { text: 'Reconciliation', icon: <ReconciliationIcon />, path: '/reconciliation' },
  { text: 'Processing', icon: <StartIcon />, path: '/processing' },
  { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
  { text: 'Users', icon: <TeamIcon />, path: '/users' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings', hasSubItems: true },
  { text: 'Order Automation', icon: <RoutingIcon />, path: '/settings/routing-rules' },
  { text: 'Delivery App', icon: <DriverDashboardIcon />, path: '/driver', hasSubItems: true, highlight: true },
];

// Role-based nav visibility (REQ-14). admin sees everything; items not
// listed here are visible to all staff. Keep in sync with the server-side
// section map in server/utils/section_auth.py.
const NAV_ITEM_ROLES: Record<string, string[]> = {
  Map: ['dispatch', 'warehouse', 'office'],
  'Fleet Live': ['dispatch', 'warehouse', 'office'],
  Quotes: ['office'],
  Orders: ['office', 'dispatch', 'warehouse'],
  Picking: ['warehouse'],
  Manifests: ['dispatch', 'warehouse'],
  Delivery: ['dispatch', 'warehouse'],
  Customers: ['office'],
  Products: ['office', 'warehouse'],
  Inventory: ['office', 'warehouse'],
  '3PL': ['office', 'warehouse'],
  Hub: ['office', 'dispatch', 'warehouse', 'driver'],
  Vehicles: ['dispatch', 'warehouse'],
  Timetables: ['dispatch', 'warehouse'],
  Zones: ['dispatch', 'warehouse'],
  'Warehouse Zones': ['warehouse'],
  Drivers: ['dispatch', 'warehouse'],
  Reconciliation: ['office', 'warehouse'],
  Processing: ['warehouse'],
  Reports: ['office', 'dispatch'],
  Users: [],
  Settings: [],
  'Order Automation': [],
  'Delivery App': ['driver', 'warehouse'],
};

// Path prefix -> allowed roles for the friendly no-access guard (admin
// passes all). First match wins; unlisted paths are open to all staff.
const PATH_ROLES: Array<[string, string[]]> = [
  ['/settings', []],
  ['/users', []],
  ['/map', ['dispatch', 'warehouse', 'office']],
  ['/fleet-map', ['dispatch', 'warehouse', 'office']],
  ['/quotes', ['office']],
  ['/customers', ['office']],
  ['/3pl', ['office', 'warehouse']],
  ['/manifests', ['dispatch', 'warehouse']],
  ['/delivery', ['dispatch', 'warehouse']],
  ['/picking', ['warehouse']],
  ['/warehouse-zones', ['warehouse']],
  ['/vehicles', ['dispatch', 'warehouse']],
  ['/timetables', ['dispatch', 'warehouse']],
  ['/zones', ['dispatch', 'warehouse']],
  ['/drivers', ['dispatch', 'warehouse']],
  ['/reports', ['office', 'dispatch']],
  ['/reconciliation', ['office', 'warehouse']],
  ['/processing', ['warehouse']],
  ['/orders', ['office', 'dispatch', 'warehouse']],
];

const roleAllowed = (role: string | undefined, roles: string[]): boolean =>
  role === 'admin' || roles.includes(role || 'office');

const getOrderSubItems = (counts: any, runs: any[] = []) => {
  // Helper function to get counts for a specific shipping method
  const getShippingMethodCounts = (shippingMethod: string) => {
    // Use real folder counts from backend if available
    const folderCounts = counts.folder_counts || {};
    const dispatchedCounts = counts.dispatched_counts || {};
    
    // Map folder names to counts - use actual backend data
    const folderMapping: { [key: string]: string } = {
      'Sydney Solvent Fleet': 'sydney_fleet',
      'Sydney Solvent Fleet Bulk': 'sydney_fleet_bulk',
      'Border Express': 'border_express',
      'Border Express Bulk': 'border_express_bulk',
      'Courier Please': 'courier_please',
      'Vellex': 'vellex',
      'Mainfreight': 'mainfreight',
      'Pick Up Instore': 'pickup'
    };
    
    const key = folderMapping[shippingMethod] || '';
    return { 
      pending: folderCounts[key] || 0,
      dispatched: dispatchedCounts[key] || 0
    };
  };

  return [
    { 
      text: 'All Orders', 
      icon: <AllOrdersIcon />, 
      path: '/orders',
      color: '#0077be' // Ocean blue
    },
    { 
      text: 'On Hold', 
      icon: <TagIcon />, 
      path: '/orders/onhold', 
      count: counts.on_hold,
      color: orderTypeColors.onHold,
      hasSubItems: true,
      subItems: [
        { text: 'Unallocated', icon: <UnallocatedIcon />, path: '/orders/onhold/unallocated', color: orderTypeColors.onHold, hideCount: true },
        { text: 'Awaiting payment', icon: <AwaitingPaymentIcon />, path: '/orders/onhold/awaiting-payment', color: orderTypeColors.onHold, hideCount: true },
        { text: 'Awaiting Stock', icon: <AwaitingStockIcon />, path: '/orders/onhold/awaiting-stock', color: orderTypeColors.onHold, hideCount: true },
        { text: 'Customer Issue', icon: <CustomerIssueIcon />, path: '/orders/onhold/customer-issue', color: orderTypeColors.onHold, hideCount: true },
        { text: 'Freight Issue', icon: <FreightIssueIcon />, path: '/orders/onhold/freight-issue', color: orderTypeColors.onHold, hideCount: true },
        { text: 'Internal Issue', icon: <InternalIssueIcon />, path: '/orders/onhold/internal-issue', color: orderTypeColors.onHold, hideCount: true }
      ]
    },
    { 
      text: 'Sydney Solvent Fleet', 
      icon: <FleetIcon />, 
      path: '/orders/sydney-fleet',
      count: getShippingMethodCounts('Sydney Solvent Fleet').pending,
      color: orderTypeColors.sydneyFleet,
      hasSubItems: true,
      subItems: [
        { text: 'Pending', icon: <PendingIcon />, path: '/orders/sydney-fleet/pending', color: orderTypeColors.sydneyFleet, count: getShippingMethodCounts('Sydney Solvent Fleet').pending },
        { text: 'Dispatched', icon: <DispatchedIcon />, path: '/orders/sydney-fleet/dispatched', color: orderTypeColors.sydneyFleet, count: getShippingMethodCounts('Sydney Solvent Fleet').dispatched },
        // One folder per upcoming fleet run (bulk runs are marked)
        ...runs.map((run) => ({
          text: `${run.route_name || run.route_number}${run.is_bulk ? ' (Bulk)' : ''}`,
          icon: <RouteIcon />,
          path: `/orders/sydney-fleet/run/${run.id}`,
          color: orderTypeColors.sydneyFleet,
          count: run.order_count
        }))
      ]
    },
    { 
      text: 'Sydney Solvent Fleet Bulk', 
      icon: <FleetIcon />, 
      path: '/orders/sydney-fleet-bulk',
      count: getShippingMethodCounts('Sydney Solvent Fleet Bulk').pending,
      color: orderTypeColors.sydneyFleetBulk,
      hasSubItems: true,
      subItems: [
        { text: 'Pending', icon: <PendingIcon />, path: '/orders/sydney-fleet-bulk/pending', color: orderTypeColors.sydneyFleetBulk, count: getShippingMethodCounts('Sydney Solvent Fleet Bulk').pending },
        { text: 'Dispatched', icon: <DispatchedIcon />, path: '/orders/sydney-fleet-bulk/dispatched', color: orderTypeColors.sydneyFleetBulk, count: getShippingMethodCounts('Sydney Solvent Fleet Bulk').dispatched }
      ]
    },
    {
      text: 'Border Express',
      icon: <TruckIcon />,
      path: '/orders/border-express',
      count: getShippingMethodCounts('Border Express').pending,
      color: orderTypeColors.borderExpress,
      hasSubItems: true,
      subItems: [
        { text: 'Pending', icon: <PendingIcon />, path: '/orders/border-express/pending', color: orderTypeColors.borderExpress, count: getShippingMethodCounts('Border Express').pending },
        { text: 'Dispatched', icon: <DispatchedIcon />, path: '/orders/border-express/dispatched', color: orderTypeColors.borderExpress, count: getShippingMethodCounts('Border Express').dispatched }
      ]
    },
    {
      text: 'Border Express Bulk',
      icon: <BulkIcon />,
      path: '/orders/border-express-bulk',
      count: getShippingMethodCounts('Border Express Bulk').pending,
      color: orderTypeColors.borderExpressBulk,
      hasSubItems: true,
      subItems: [
        { text: 'Pending', icon: <PendingIcon />, path: '/orders/border-express-bulk/pending', color: orderTypeColors.borderExpressBulk, count: getShippingMethodCounts('Border Express Bulk').pending },
        { text: 'Dispatched', icon: <DispatchedIcon />, path: '/orders/border-express-bulk/dispatched', color: orderTypeColors.borderExpressBulk, count: getShippingMethodCounts('Border Express Bulk').dispatched }
      ]
    },
    {
      text: 'Courier Please',
      icon: <TruckIcon />,
      path: '/orders/courier-please',
      count: getShippingMethodCounts('Courier Please').pending,
      color: orderTypeColors.courierPlease,
      hasSubItems: true,
      subItems: [
        { text: 'Pending', icon: <PendingIcon />, path: '/orders/courier-please/pending', color: orderTypeColors.courierPlease, count: getShippingMethodCounts('Courier Please').pending },
        { text: 'Dispatched', icon: <DispatchedIcon />, path: '/orders/courier-please/dispatched', color: orderTypeColors.courierPlease, count: getShippingMethodCounts('Courier Please').dispatched }
      ]
    },
    {
      text: 'Vellex',
      icon: <TruckIcon />,
      path: '/orders/vellex',
      count: getShippingMethodCounts('Vellex').pending,
      color: orderTypeColors.vellex,
      hasSubItems: true,
      subItems: [
        { text: 'Pending', icon: <PendingIcon />, path: '/orders/vellex/pending', color: orderTypeColors.vellex, count: getShippingMethodCounts('Vellex').pending },
        { text: 'Dispatched', icon: <DispatchedIcon />, path: '/orders/vellex/dispatched', color: orderTypeColors.vellex, count: getShippingMethodCounts('Vellex').dispatched }
      ]
    },
    {
      text: 'Mainfreight',
      icon: <TruckIcon />,
      path: '/orders/mainfreight',
      count: getShippingMethodCounts('Mainfreight').pending,
      color: orderTypeColors.mainfreight,
      hasSubItems: true,
      subItems: [
        { text: 'Pending', icon: <PendingIcon />, path: '/orders/mainfreight/pending', color: orderTypeColors.mainfreight, count: getShippingMethodCounts('Mainfreight').pending },
        { text: 'Dispatched', icon: <DispatchedIcon />, path: '/orders/mainfreight/dispatched', color: orderTypeColors.mainfreight, count: getShippingMethodCounts('Mainfreight').dispatched }
      ]
    },
    {
      text: 'Pick Up Instore',
      icon: <StoreIcon />,
      path: '/orders/pickup-instore',
      count: getShippingMethodCounts('Pick Up Instore').pending,
      color: orderTypeColors.pickupInstore,
      hasSubItems: true,
      subItems: [
        { text: 'Pending', icon: <PendingIcon />, path: '/orders/pickup-instore/pending', color: orderTypeColors.pickupInstore, count: getShippingMethodCounts('Pick Up Instore').pending },
        { text: 'Dispatched', icon: <DispatchedIcon />, path: '/orders/pickup-instore/dispatched', color: orderTypeColors.pickupInstore, count: getShippingMethodCounts('Pick Up Instore').dispatched },
        { text: 'Ready To Pickup', icon: <ReadyIcon />, path: '/orders/pickup-instore/ready', color: orderTypeColors.pickupInstore, count: (counts.ready_to_pickup_counts || {}).pickup || 0 }
      ]
    },
    { 
      text: 'Returns', 
      icon: <ManifestsIcon />, 
      path: '/orders/returns', 
      count: counts.returned,
      color: orderTypeColors.returns
    },
    { 
      text: 'Cancelled', 
      icon: <ErrorIcon />, 
      path: '/orders/cancelled', 
      count: counts.cancelled,
      color: orderTypeColors.cancelled
    }
  ];
};

const getQuoteSubItems = (counts: any) => {
  return [
    { 
      text: 'Working On', 
      icon: <WorkIcon />, 
      path: '/quotes?status=working_on',
      color: '#ff9800' // Orange
    },
    { 
      text: 'Sent', 
      icon: <SentIcon />, 
      path: '/quotes?status=sent',
      color: '#2196f3' // Blue
    },
    { 
      text: 'Need Follow Up', 
      icon: <FollowUpIcon />, 
      path: '/quotes?status=need_follow_up',
      color: '#f44336' // Red
    },
    { 
      text: 'Order Quotes', 
      icon: <QuoteIcon />, 
      path: '/orders/quote',
      count: counts.quote || 0,
      color: orderTypeColors.quote
    }
  ];
};

const getDriverSubItems = () => {
  return [
    { 
      text: 'Delivery Dashboard', 
      icon: <DriverDashboardIcon />, 
      path: '/driver/dashboard',
      color: '#1976d2' // Blue
    },
    { 
      text: 'My Routes', 
      icon: <RouteIcon />, 
      path: '/driver/routes',
      color: '#1976d2' // Blue
    },
    { 
      text: 'My Deliveries', 
      icon: <DeliveryTaskIcon />, 
      path: '/driver/deliveries',
      color: '#1976d2' // Blue
    }
  ];
};

export function Layout({ children }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>('Orders');
  const [openNestedMenu, setOpenNestedMenu] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, isAuthenticated } = useAuth();
  
  // Show logout if authenticated via context or token exists
  const showLogout = isAuthenticated || apiService.isAuthenticated();
  
  // Fetch dynamic order counts
  const { counts, loading: countsLoading, refetch: refetchCounts } = useOrderCounts();
  
  // Fetch upcoming fleet runs for the sidebar run folders
  const [runs, setRuns] = useState<any[]>([]);
  useEffect(() => {
    const fetchRuns = async () => {
      try {
        const response = await apiService.client.get('/routes/sidebar');
        setRuns(response.data.runs || []);
      } catch (err) {
        console.error('Error fetching fleet runs:', err);
      }
    };
    fetchRuns();
    window.addEventListener('orderStatusChanged', fetchRuns);
    return () => window.removeEventListener('orderStatusChanged', fetchRuns);
  }, []);
  
  // Get dynamic order sub items
  const orderSubItems = getOrderSubItems(counts, runs);
  
  // Get quote sub items
  const quoteSubItems = getQuoteSubItems(counts);
  
  // Get driver sub items
  const driverSubItems = getDriverSubItems();

  // REQ-14: role-filtered nav + friendly no-access guard for gated paths.
  const userRole = user?.role || 'office';
  const visibleTopLevelItems = topLevelItems.filter((item) => {
    const roles = NAV_ITEM_ROLES[item.text];
    return !roles || roleAllowed(userRole, roles);
  });
  const pathRule = PATH_ROLES.find(([prefix]) => location.pathname.startsWith(prefix));
  const pathAllowed = !pathRule || roleAllowed(userRole, pathRule[1]);

  // Track last pathname to only auto-expand on navigation, not on every render
  const lastPathnameRef = useRef<string>(location.pathname);

  // Auto-expand nested menus when on a nested path
  useEffect(() => {
    // Only process if pathname actually changed (navigation occurred)
    if (lastPathnameRef.current === location.pathname) {
      return;
    }
    
    lastPathnameRef.current = location.pathname;
    
    // Check if we're on an "On Hold" sub-item path
    const onHoldSubItems = orderSubItems.find(item => item.text === 'On Hold')?.subItems || [];
    const isOnHoldSubPath = onHoldSubItems.some(item => location.pathname === item.path);
    
    if (isOnHoldSubPath) {
      // Only auto-expand if we're actually on a nested path (navigation occurred)
      setOpenSubmenu('Orders');
      setOpenNestedMenu('On Hold');
    } else if (location.pathname === '/orders/quote') {
      // Auto-expand Quotes menu when on Order Quotes page
      setOpenSubmenu('Quotes');
      setOpenNestedMenu(null);
    } else if (!location.pathname.includes('/orders')) {
      // Only close nested menu if we've navigated completely away from Orders section
      setOpenNestedMenu(null);
    }
    // Don't interfere if we're still in Orders section but not on a nested path
  }, [location.pathname, orderSubItems]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleSubmenuToggle = (menuName: string) => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      setOpenSubmenu(menuName);
      setOpenNestedMenu(null); // Close nested menu when switching
      return;
    }
    
    if (openSubmenu === menuName) {
      setOpenSubmenu(null);
      setOpenNestedMenu(null); // Close nested menu when closing submenu
    } else {
      setOpenSubmenu(menuName);
      setOpenNestedMenu(null); // Close nested menu when switching to different submenu
    }
  };

  const handleNestedMenuToggle = (menuName: string) => {
    if (openNestedMenu === menuName) {
      setOpenNestedMenu(null);
    } else {
      setOpenNestedMenu(menuName);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, navigate to login
      navigate('/login', { replace: true });
    }
  };

  const currentDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : expandedDrawerWidth;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e5e8ed',
          color: '#1a2332',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: '60px !important', px: { xs: 2, sm: 3 } }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: '#475569' }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{
            flexGrow: 1,
            fontSize: '1rem',
            fontWeight: 600,
            color: '#1a2332',
            letterSpacing: '-0.01em',
          }}>
            {location.pathname === '/' || location.pathname === '/dashboard'
              ? 'Dashboard'
              : location.pathname.split('/').filter(Boolean).map(part =>
                  part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' ')
                ).join(' / ')}
          </Typography>

          {showLogout && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user && (
                <Box sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 1.5,
                  mr: 1,
                }}>
                  <Box sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '8px',
                    backgroundColor: '#1a7a7a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                  }}>
                    {(user.first_name?.[0] || user.username?.[0] || 'U').toUpperCase()}
                  </Box>
                  <Box>
                    <Typography sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: '#1a2332',
                      lineHeight: 1.3,
                    }}>
                      {user.first_name && user.last_name
                        ? `${user.first_name} ${user.last_name}`
                        : user.username || 'User'}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.6875rem',
                      color: '#94a3b8',
                      lineHeight: 1.2,
                    }}>
                      {user.role || 'Administrator'}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Tooltip title="Sign out">
                <IconButton
                  onClick={handleLogout}
                  sx={{
                    color: '#94a3b8',
                    borderRadius: '8px',
                    width: 36,
                    height: 36,
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                      color: '#dc3545',
                    },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: '1.2rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ 
          width: { sm: currentDrawerWidth }, 
          flexShrink: { sm: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          sx={{
            display: { xs: isMobile ? 'block' : 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: isMobile ? expandedDrawerWidth : currentDrawerWidth,
              backgroundColor: sidebarColors.bg,
              borderRight: 'none',
              overflow: sidebarCollapsed && !isMobile ? 'visible' : 'auto',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: '#1e293b', borderRadius: '100px' },
            },
          }}
        >
          <div>
            <Toolbar sx={{
              display: 'flex',
              justifyContent: sidebarCollapsed && !isMobile ? 'center' : 'space-between',
              alignItems: 'center',
              minHeight: '60px !important',
              px: sidebarCollapsed && !isMobile ? 1 : 2,
              borderBottom: `1px solid ${sidebarColors.border}`,
              backgroundColor: sidebarColors.headerBg,
            }}>
              {(!sidebarCollapsed || isMobile) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 0.5, flexGrow: 1 }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #2d9d9d, #1a7a7a)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.875rem' }}>SS</Typography>
                  </Box>
                  <Box>
                    <Typography noWrap sx={{
                      fontWeight: 700,
                      fontSize: '0.9375rem',
                      color: '#f1f5f9',
                      letterSpacing: '-0.02em',
                      lineHeight: 1.2,
                    }}>
                      Sydney Solvent
                    </Typography>
                    <Typography noWrap sx={{
                      fontSize: '0.625rem',
                      color: '#64748b',
                      fontWeight: 500,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}>
                      Fulfillment Center
                    </Typography>
                  </Box>
                </Box>
              )}
              {sidebarCollapsed && !isMobile && (
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #2d9d9d, #1a7a7a)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.875rem' }}>SS</Typography>
                </Box>
              )}
              {!isMobile && (
                <IconButton onClick={toggleSidebar} size="small" sx={{
                  color: '#64748b',
                  '&:hover': { color: '#94a3b8', backgroundColor: sidebarColors.bgHover },
                  width: 28,
                  height: 28,
                }}>
                  {sidebarCollapsed ? <ChevronRightIcon sx={{ fontSize: '1rem' }} /> : <ChevronLeftIcon sx={{ fontSize: '1rem' }} />}
                </IconButton>
              )}
            </Toolbar>
            <List sx={{ paddingTop: '8px', px: sidebarCollapsed && !isMobile ? 0.5 : 1 }}>
              {visibleTopLevelItems.map((item) => (
                <React.Fragment key={item.text}>
                  <Tooltip 
                    title={sidebarCollapsed && !isMobile ? item.text : ""} 
                    placement="right" 
                    arrow
                  >
                    <ListItem
                      button
                      onClick={() => {
                        if (item.hasSubItems) {
                          handleSubmenuToggle(item.text);
                        } else {
                          setOpenNestedMenu(null); // Close nested menu when navigating away
                          navigate(item.path);
                          if (isMobile) {
                            setMobileOpen(false);
                          }
                        }
                      }}
                      selected={!item.hasSubItems && (location.pathname === item.path || (item.text === 'Quotes' && (location.pathname.includes('/quotes') || location.pathname === '/orders/quote')))}
                      sx={{
                        py: 0.85,
                        px: sidebarCollapsed && !isMobile ? 1 : 1.5,
                        mb: 0.25,
                        borderRadius: '8px',
                        borderLeft: 'none',
                        color: sidebarColors.text,
                        backgroundColor: (location.pathname === item.path ||
                          (item.text === 'Orders' && location.pathname.includes('/orders')) ||
                          (item.text === 'Quotes' && (location.pathname.includes('/quotes') || location.pathname === '/orders/quote')) ||
                          (item.text === 'Delivery App' && location.pathname.includes('/driver')) ||
                          (item.text === 'Reports' && location.pathname === '/reports') ||
                          (item.text === 'Order Automation' && location.pathname.includes('/settings/routing-rules')))
                          ? sidebarColors.bgActive : 'transparent',
                        '&.Mui-selected': {
                          backgroundColor: sidebarColors.bgActive,
                          '& .MuiListItemIcon-root': {
                            color: sidebarColors.accent,
                          },
                          '& .MuiListItemText-primary': {
                            fontWeight: 600,
                            color: sidebarColors.textActive,
                          },
                        },
                        '&:hover': {
                          backgroundColor: sidebarColors.bgHover,
                        },
                        justifyContent: sidebarCollapsed && !isMobile ? 'center' : 'initial',
                        transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: sidebarCollapsed && !isMobile ? 0 : 36,
                        color: (location.pathname === item.path ||
                          (item.text === 'Orders' && location.pathname.includes('/orders')) ||
                          (item.text === 'Quotes' && (location.pathname.includes('/quotes') || location.pathname === '/orders/quote')) ||
                          (item.text === 'Delivery App' && location.pathname.includes('/driver')) ||
                          (item.text === 'Reports' && location.pathname === '/reports') ||
                          (item.text === 'Order Automation' && location.pathname.includes('/settings/routing-rules')))
                          ? sidebarColors.accent : sidebarColors.text,
                        '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      {(!sidebarCollapsed || isMobile) && (
                        <>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontSize: '0.8125rem',
                              fontWeight: (location.pathname === item.path ||
                                (item.text === 'Orders' && location.pathname.includes('/orders')) ||
                                (item.text === 'Quotes' && (location.pathname.includes('/quotes') || location.pathname === '/orders/quote')) ||
                                (item.text === 'Delivery App' && location.pathname.includes('/driver')) ||
                                (item.text === 'Reports' && location.pathname === '/reports') ||
                          (item.text === 'Order Automation' && location.pathname.includes('/settings/routing-rules')))
                                ? 600 : 500,
                              color: (location.pathname === item.path ||
                                (item.text === 'Orders' && location.pathname.includes('/orders')) ||
                                (item.text === 'Quotes' && (location.pathname.includes('/quotes') || location.pathname === '/orders/quote')) ||
                                (item.text === 'Delivery App' && location.pathname.includes('/driver')) ||
                                (item.text === 'Reports' && location.pathname === '/reports') ||
                          (item.text === 'Order Automation' && location.pathname.includes('/settings/routing-rules')))
                                ? sidebarColors.textActive : sidebarColors.text,
                            }}
                          />
                          {item.text === 'Orders' && counts.pending > 0 && (
                            <Chip
                              label={counts.pending}
                              size="small"
                              sx={{
                                height: 20,
                                minWidth: 28,
                                backgroundColor: 'rgba(229, 147, 10, 0.2)',
                                color: '#f59e0b',
                                fontWeight: 600,
                                fontSize: '0.6875rem',
                                mr: 0.5,
                                '& .MuiChip-label': { padding: '0 6px' },
                              }}
                            />
                          )}
                          {item.text === 'Delivery App' && (
                            <Chip
                              label="App"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.625rem',
                                backgroundColor: 'rgba(45, 157, 157, 0.2)',
                                color: sidebarColors.accent,
                                fontWeight: 600,
                                mr: 0.5,
                                '& .MuiChip-label': { padding: '0 6px' },
                              }}
                            />
                          )}
                          {item.hasSubItems && (
                            openSubmenu === item.text
                              ? <ExpandLess sx={{ color: sidebarColors.text, fontSize: '1.1rem' }} />
                              : <ExpandMore sx={{ color: sidebarColors.text, fontSize: '1.1rem' }} />
                          )}
                        </>
                      )}
                    </ListItem>
                  </Tooltip>
                  
                  {item.text === 'Orders' && (
                    <Collapse in={openSubmenu === 'Orders' && (!sidebarCollapsed || isMobile)} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {orderSubItems.map((subItem) => (
                          <React.Fragment key={subItem.text}>
                            <ListItem
                              button
                              onClick={() => {
                                if (subItem.hasSubItems) {
                                  handleNestedMenuToggle(subItem.text);
                                } else {
                                  navigate(subItem.path);
                                  if (isMobile) {
                                    setMobileOpen(false);
                                  }
                                }
                              }}
                              selected={!subItem.hasSubItems && location.pathname === subItem.path ||
                                (subItem.hasSubItems && subItem.subItems?.some(nested => location.pathname === nested.path))}
                              sx={{
                                pl: 3.5,
                                py: 0.6,
                                mb: 0.15,
                                mx: 0.5,
                                borderRadius: '6px',
                                borderLeft: 'none',
                                color: sidebarColors.text,
                                '&.Mui-selected': {
                                  backgroundColor: sidebarColors.bgActive,
                                  '& .MuiListItemIcon-root': {
                                    color: subItem.color || sidebarColors.accent,
                                  },
                                  '& .MuiListItemText-primary': {
                                    fontWeight: 600,
                                    color: sidebarColors.textActive,
                                  },
                                },
                                '&:hover': {
                                  backgroundColor: sidebarColors.bgHover,
                                },
                              }}
                            >
                              <ListItemIcon sx={{
                                minWidth: 28,
                                color: (location.pathname === subItem.path ||
                                  (subItem.hasSubItems && location.pathname.includes(subItem.path)))
                                  ? subItem.color || sidebarColors.accent : sidebarColors.text,
                                '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                              }}>
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={subItem.text}
                                primaryTypographyProps={{
                                  fontSize: '0.78125rem',
                                  fontWeight: (location.pathname === subItem.path ||
                                    (subItem.hasSubItems && location.pathname.includes(subItem.path)))
                                    ? 600 : 400,
                                  color: (location.pathname === subItem.path ||
                                    (subItem.hasSubItems && location.pathname.includes(subItem.path)))
                                    ? sidebarColors.textActive : sidebarColors.text,
                                }}
                              />
                              
                              {subItem.count !== undefined && subItem.text !== 'All Orders' && (
                                <Chip
                                  label={subItem.count}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    minWidth: 28,
                                    backgroundColor: `${subItem.color || sidebarColors.accent}25`,
                                    color: subItem.color || sidebarColors.accent,
                                    fontWeight: 600,
                                    fontSize: '0.6875rem',
                                    ml: 'auto',
                                    '& .MuiChip-label': { padding: '0 5px' },
                                  }}
                                />
                              )}

                              {subItem.hasSubItems && (
                                openNestedMenu === subItem.text
                                  ? <ExpandLess sx={{ color: sidebarColors.text, fontSize: '1rem' }} />
                                  : <ExpandMore sx={{ color: sidebarColors.text, fontSize: '1rem' }} />
                              )}
                            </ListItem>
                            
                            {subItem.hasSubItems && (
                              <Collapse in={openNestedMenu === subItem.text} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                  {subItem.subItems?.map((nestedItem) => (
                                    <ListItem
                                      button
                                      key={nestedItem.text}
                                      onClick={() => {
                                        navigate(nestedItem.path);
                                        if (isMobile) {
                                          setMobileOpen(false);
                                        }
                                      }}
                                      selected={location.pathname === nestedItem.path}
                                      sx={{
                                        pl: 5.5,
                                        py: 0.5,
                                        mx: 0.5,
                                        borderRadius: '6px',
                                        borderLeft: 'none',
                                        color: sidebarColors.text,
                                        '&.Mui-selected': {
                                          backgroundColor: sidebarColors.bgActive,
                                          '& .MuiListItemIcon-root': { color: nestedItem.color || sidebarColors.accent },
                                          '& .MuiListItemText-primary': { fontWeight: 600, color: sidebarColors.textActive },
                                        },
                                        '&:hover': { backgroundColor: sidebarColors.bgHover },
                                      }}
                                    >
                                      <ListItemIcon sx={{
                                        minWidth: 26,
                                        color: location.pathname === nestedItem.path ? nestedItem.color || sidebarColors.accent : sidebarColors.text,
                                        '& .MuiSvgIcon-root': { fontSize: '1rem' },
                                      }}>
                                        {nestedItem.icon}
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={nestedItem.text}
                                        primaryTypographyProps={{
                                          fontSize: '0.75rem',
                                          fontWeight: location.pathname === nestedItem.path ? 600 : 400,
                                          color: location.pathname === nestedItem.path ? sidebarColors.textActive : sidebarColors.text,
                                        }}
                                      />
                                      
                                      {!nestedItem.hideCount && (nestedItem as {count?: number}).count !== undefined && (
                                        <Chip
                                          label={(nestedItem as {count?: number}).count}
                                          size="small"
                                          sx={{
                                            height: 16,
                                            minWidth: 24,
                                            backgroundColor: `${nestedItem.color || sidebarColors.accent}25`,
                                            color: nestedItem.color || sidebarColors.accent,
                                            fontWeight: 600,
                                            fontSize: '0.625rem',
                                            ml: 'auto',
                                            '& .MuiChip-label': { padding: '0 4px' },
                                          }}
                                        />
                                      )}
                                    </ListItem>
                                  ))}
                                </List>
                              </Collapse>
                            )}
                          </React.Fragment>
                        ))}
                      </List>
                    </Collapse>
                  )}
                  
                  {item.text === 'Quotes' && (
                    <Collapse in={openSubmenu === 'Quotes' && (!sidebarCollapsed || isMobile)} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {quoteSubItems.map((subItem) => (
                          <ListItem
                            button
                            key={subItem.text}
                            onClick={() => {
                              navigate(subItem.path);
                              if (isMobile) {
                                setMobileOpen(false);
                              }
                            }}
                            selected={(location.pathname.includes('/quotes') && new URLSearchParams(location.search).get('status') === subItem.path.split('=')[1]) || (subItem.path === '/orders/quote' && location.pathname === '/orders/quote')}
                            sx={{
                              pl: 3.5,
                              py: 0.6,
                              mx: 0.5,
                              borderRadius: '6px',
                              borderLeft: 'none',
                              color: sidebarColors.text,
                              '&.Mui-selected': {
                                backgroundColor: sidebarColors.bgActive,
                                '& .MuiListItemIcon-root': { color: subItem.color || sidebarColors.accent },
                                '& .MuiListItemText-primary': { fontWeight: 600, color: sidebarColors.textActive },
                              },
                              '&:hover': { backgroundColor: sidebarColors.bgHover },
                            }}
                          >
                            <ListItemIcon sx={{
                              minWidth: 28,
                              color: ((location.pathname.includes('/quotes') && new URLSearchParams(location.search).get('status') === subItem.path.split('=')[1]) || (subItem.path === '/orders/quote' && location.pathname === '/orders/quote')) ? subItem.color || sidebarColors.accent : sidebarColors.text,
                              '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                            }}>
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.text}
                              primaryTypographyProps={{
                                fontSize: '0.78125rem',
                                fontWeight: ((location.pathname.includes('/quotes') && new URLSearchParams(location.search).get('status') === subItem.path.split('=')[1]) || (subItem.path === '/orders/quote' && location.pathname === '/orders/quote')) ? 600 : 400,
                                color: ((location.pathname.includes('/quotes') && new URLSearchParams(location.search).get('status') === subItem.path.split('=')[1]) || (subItem.path === '/orders/quote' && location.pathname === '/orders/quote')) ? sidebarColors.textActive : sidebarColors.text,
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                  
                  {item.text === 'Delivery App' && (
                    <Collapse in={openSubmenu === 'Delivery App' && (!sidebarCollapsed || isMobile)} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {driverSubItems.map((subItem) => (
                          <ListItem
                            button
                            key={subItem.text}
                            onClick={() => {
                              navigate(subItem.path);
                              if (isMobile) {
                                setMobileOpen(false);
                              }
                            }}
                            selected={location.pathname === subItem.path}
                            sx={{
                              pl: 3.5,
                              py: 0.6,
                              mx: 0.5,
                              borderRadius: '6px',
                              borderLeft: 'none',
                              color: sidebarColors.text,
                              '&.Mui-selected': {
                                backgroundColor: sidebarColors.bgActive,
                                '& .MuiListItemIcon-root': { color: subItem.color || sidebarColors.accent },
                                '& .MuiListItemText-primary': { fontWeight: 600, color: sidebarColors.textActive },
                              },
                              '&:hover': { backgroundColor: sidebarColors.bgHover },
                            }}
                          >
                            <ListItemIcon sx={{
                              minWidth: 28,
                              color: location.pathname === subItem.path ? subItem.color || sidebarColors.accent : sidebarColors.text,
                              '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                            }}>
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.text}
                              primaryTypographyProps={{
                                fontSize: '0.78125rem',
                                fontWeight: location.pathname === subItem.path ? 600 : 400,
                                color: location.pathname === subItem.path ? sidebarColors.textActive : sidebarColors.text,
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                  {item.text === '3PL' && (
                    <Collapse in={openSubmenu === '3PL' && (!sidebarCollapsed || isMobile)} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {[
                          { text: 'Dashboard', icon: <DashboardIcon />, path: '/3pl' },
                          { text: 'Clients', icon: <TeamIcon />, path: '/3pl/clients' },
                          { text: 'Movements', icon: <AllOrdersIcon />, path: '/3pl/movements' },
                          { text: 'Approvals', icon: <PendingIcon />, path: '/3pl/approvals' },
                          { text: 'Stock on Hand', icon: <InventoryIconMui />, path: '/3pl/stock' },
                          { text: 'By SKU', icon: <InventoryIconMui />, path: '/3pl/sku-summary' },
                          { text: 'Products', icon: <InventoryIconMui />, path: '/3pl/products' },
                          { text: 'Stock Count', icon: <StockCountIcon />, path: '/3pl/stock-count' },
                          { text: 'Invoicing', icon: <InvoicingIcon />, path: '/3pl/invoicing' },
                          { text: 'Statement', icon: <StatementIcon />, path: '/3pl/statement' },
                          { text: 'Health Check', icon: <HealthCheckIcon />, path: '/3pl/health-check' },
                          { text: 'Client Portal', icon: <WarehouseIcon />, path: '/3pl/portal' },
                        ].map((subItem) => (
                          <ListItem
                            button
                            key={subItem.text}
                            onClick={() => {
                              navigate(subItem.path);
                              if (isMobile) {
                                setMobileOpen(false);
                              }
                            }}
                            selected={location.pathname === subItem.path}
                            sx={{
                              pl: 3.5,
                              py: 0.6,
                              mx: 0.5,
                              borderRadius: '6px',
                              borderLeft: 'none',
                              color: sidebarColors.text,
                              '&.Mui-selected': {
                                backgroundColor: sidebarColors.bgActive,
                                '& .MuiListItemIcon-root': { color: sidebarColors.accent },
                                '& .MuiListItemText-primary': { fontWeight: 600, color: sidebarColors.textActive },
                              },
                              '&:hover': { backgroundColor: sidebarColors.bgHover },
                            }}
                          >
                            <ListItemIcon sx={{
                              minWidth: 28,
                              color: location.pathname === subItem.path ? sidebarColors.accent : sidebarColors.text,
                              '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                            }}>
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.text}
                              primaryTypographyProps={{
                                fontSize: '0.78125rem',
                                fontWeight: location.pathname === subItem.path ? 600 : 400,
                                color: location.pathname === subItem.path ? sidebarColors.textActive : sidebarColors.text,
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                  {item.text === 'Hub' && (
                    <Collapse in={openSubmenu === 'Hub' && (!sidebarCollapsed || isMobile)} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {[
                          { text: 'Job Tracker', icon: <WorkIcon />, path: '/hub/jobs' },
                          { text: 'Time Clock', icon: <ClockIcon />, path: '/hub/clock' },
                        ].map((subItem) => (
                          <ListItem
                            button
                            key={subItem.text}
                            onClick={() => {
                              navigate(subItem.path);
                              if (isMobile) {
                                setMobileOpen(false);
                              }
                            }}
                            selected={location.pathname === subItem.path}
                            sx={{
                              pl: 3.5,
                              py: 0.6,
                              mx: 0.5,
                              borderRadius: '6px',
                              borderLeft: 'none',
                              color: sidebarColors.text,
                              '&.Mui-selected': {
                                backgroundColor: sidebarColors.bgActive,
                                '& .MuiListItemIcon-root': { color: sidebarColors.accent },
                                '& .MuiListItemText-primary': { fontWeight: 600, color: sidebarColors.textActive },
                              },
                              '&:hover': { backgroundColor: sidebarColors.bgHover },
                            }}
                          >
                            <ListItemIcon sx={{
                              minWidth: 28,
                              color: location.pathname === subItem.path ? sidebarColors.accent : sidebarColors.text,
                              '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                            }}>
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.text}
                              primaryTypographyProps={{
                                fontSize: '0.78125rem',
                                fontWeight: location.pathname === subItem.path ? 600 : 400,
                                color: location.pathname === subItem.path ? sidebarColors.textActive : sidebarColors.text,
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                  {item.text === 'Settings' && (
                    <Collapse in={openSubmenu === 'Settings' && (!sidebarCollapsed || isMobile)} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {[
                          { text: 'General', icon: <SettingsIcon />, path: '/settings', color: sidebarColors.accent },
                          { text: 'Order Automation', icon: <RoutingIcon />, path: '/settings/routing-rules', color: sidebarColors.accent },
                          { text: 'Border Express', icon: <CourierIcon />, path: '/settings/courier/border-express', color: '#f97316' },
                          { text: 'Courier Please', icon: <CourierIcon />, path: '/settings/courier', color: sidebarColors.accent },
                          { text: 'Vellex', icon: <CourierIcon />, path: '/settings/courier/vellex', color: '#673ab7' },
                          { text: 'Mainfreight (Chemcouriers)', icon: <CourierIcon />, path: '/settings/courier/mainfreight', color: '#1565c0' },
                          { text: 'MYOB', icon: <MyobIcon />, path: '/settings/myob', color: sidebarColors.accent },
                          { text: 'Marketplaces', icon: <StorefrontIcon />, path: '/settings/marketplaces', color: sidebarColors.accent },
                          { text: 'Shipping', icon: <ShippingIcon />, path: '/settings/shipping', color: sidebarColors.accent },
                          { text: 'Email Templates', icon: <EmailIcon />, path: '/settings/email-templates', color: sidebarColors.accent },
                        ].map((subItem) => (
                          <ListItem
                            button
                            key={subItem.text}
                            onClick={() => {
                              navigate(subItem.path);
                              if (isMobile) {
                                setMobileOpen(false);
                              }
                            }}
                            selected={location.pathname === subItem.path}
                            sx={{
                              pl: 3.5,
                              py: 0.6,
                              mx: 0.5,
                              borderRadius: '6px',
                              borderLeft: 'none',
                              color: sidebarColors.text,
                              '&.Mui-selected': {
                                backgroundColor: sidebarColors.bgActive,
                                '& .MuiListItemIcon-root': { color: subItem.color || sidebarColors.accent },
                                '& .MuiListItemText-primary': { fontWeight: 600, color: sidebarColors.textActive },
                              },
                              '&:hover': { backgroundColor: sidebarColors.bgHover },
                            }}
                          >
                            <ListItemIcon sx={{
                              minWidth: 28,
                              color: location.pathname === subItem.path ? subItem.color || sidebarColors.accent : sidebarColors.text,
                              '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                            }}>
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.text}
                              primaryTypographyProps={{
                                fontSize: '0.78125rem',
                                fontWeight: location.pathname === subItem.path ? 600 : 400,
                                color: location.pathname === subItem.path ? sidebarColors.textActive : sidebarColors.text,
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              ))}
            </List>
          </div>
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          mt: '60px',
          backgroundColor: '#f1f3f6',
          minHeight: 'calc(100vh - 60px)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {pathAllowed ? children : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <Paper sx={{ p: 4, maxWidth: 420, textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom>No access</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your role ({userRole}) doesn't have access to this section.
                Ask an admin if you think you should.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/')}>Back to Dashboard</Button>
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
}
